import { useRef } from "react";
import { motion } from "motion/react";
import {
  CheckCircle2,
  MessageCircle,
  Calendar,
  Monitor,
  Users,
  BookOpen,
  Award,
  ArrowRight,
  PlayCircle,
  FileText,
  Globe,
} from "lucide-react";
import StarRating from "../../Components/LandingPage/StarRating";
import FAQItem from "../../Components/LandingPage/SPM2026/FAQItem";
import RegistrationForm from "../../Components/LandingPage/SPM2026/RegistrationForm";
import {
  WA_LINK,
  DARK,
  STEP_ILLUSTRATIONS,
  SUBJECT_ICONS,
  BENEFITS,
  PAIN_POINTS,
  SUBJECTS,
  TEACHERS,
  TESTIMONIALS,
  FAQS,
} from "../../Data/LandingPage/SPM2026Data";
import { Navbar } from "../../Components/Navbar";

// ── Constants ─────────────────────────────────────────────────────────────────

const IMG_HERO =
  "https://images.unsplash.com/photo-1574738102321-f8b42792a5f0?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";
const IMG_PORTAL =
  "https://images.unsplash.com/photo-1610484826967-09c5720778c7?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=900&q=80";
const IMG_TESTIMONIAL_BG =
  "https://images.unsplash.com/photo-1758270705290-62b6294dd044?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1400&q=80";

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function SPM2026Page() {
  const formRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <div className="min-h-screen bg-white font-sans pt-20">
        <Navbar />
        {/* ── 1. HERO ──────────────────────────────────────────────────────────── */}
        <section
          className="relative overflow-hidden min-h-screen flex items-center"
          style={{ backgroundColor: DARK }}
        >
          {/* Background decoration */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-emerald-500/10 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-teal-400/10 blur-3xl" />
            <div
              className="absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
                backgroundSize: "40px 40px",
              }}
            />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 w-full">
            <div className="grid lg:grid-cols-[1fr_400px] gap-8 lg:gap-20 items-center">
              {/* Left — copy */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 mb-6">
                    <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
                    <span className="text-[13px] text-emerald-200 uppercase tracking-widest font-bold">
                      100% Free · Online Portal · Recorded Lessons
                    </span>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.08 }}
                >
                  <p className="text-[13px] text-emerald-300 uppercase tracking-[0.2em] mb-3 font-bold">
                    2026 SPM Free A+ Scoring Notes & Tips
                  </p>
                  <h1 className="text-[40px] lg:text-[50px] text-white leading-[1.05] mb-5 tracking-tight font-black">
                    Study Smart. Prepare Early.
                    <br />
                    Score Better.
                  </h1>
                  <p className="text-[18px] text-white/65 mb-10 max-w-xl leading-relaxed font-normal">
                    Learn from experienced educators and get access to revision
                    notes, recorded lessons, exam tips and scoring strategies —
                    all in one online portal.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 }}
                  className="flex flex-wrap gap-3 mb-10"
                >
                  {[
                    {
                      icon: <Users className="w-3.5 h-3.5" />,
                      text: "6 Experienced Teachers",
                    },
                    {
                      icon: <BookOpen className="w-3.5 h-3.5" />,
                      text: "7 SPM Core Subjects",
                    },
                    {
                      icon: <Award className="w-3.5 h-3.5" />,
                      text: "100% Free",
                    },
                    {
                      icon: <Globe className="w-3.5 h-3.5" />,
                      text: "Online Portal",
                    },
                  ].map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-[13px] font-semibold"
                    >
                      <span className="text-emerald-300">{s.icon}</span>{" "}
                      {s.text}
                    </div>
                  ))}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="flex flex-col gap-2.5 mb-10 text-[15px]"
                >
                  {[
                    // {
                    //   icon: <Calendar className="w-4 h-4" />,
                    //   text: "Available from October 2026",
                    // },
                    {
                      icon: <Monitor className="w-4 h-4" />,
                      text: "SCS Online Study Portal",
                    },
                    {
                      icon: <PlayCircle className="w-4 h-4" />,
                      text: "Recorded Lessons + Revision Notes + Exam Tips",
                    },
                  ].map((info, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 text-white/75 font-medium"
                    >
                      <span className="text-emerald-300">{info.icon}</span>{" "}
                      {info.text}
                    </div>
                  ))}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                  className="flex flex-col sm:flex-row gap-3"
                >
                  <button
                    onClick={scrollToForm}
                    className="px-8 py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-[15px] font-extrabold shadow-2xl shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-1 transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    🎓 Register for FREE <ArrowRight className="w-5 h-5" />
                  </button>
                  <a
                    href={WA_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-4 rounded-2xl border border-white/30 text-white text-[14px] font-semibold hover:bg-white/10 transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <MessageCircle className="w-4 h-4 text-green-400" />{" "}
                    WhatsApp Enquiry
                  </a>
                </motion.div>
              </div>

              {/* Right — student photo */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="hidden lg:block relative"
              >
                <div className="absolute inset-0 rounded-3xl bg-emerald-500/10 blur-2xl scale-110" />
                <div className="relative w-full h-[520px] rounded-3xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10">
                  <img
                    src={IMG_HERO}
                    alt="Student studying with headphones"
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A2218]/85 via-[#0A2218]/10 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4">
                    <p className="text-white text-[14px] font-bold">
                      🏆 Expert Teachers · Free Notes · Proven Strategies
                    </p>
                    <p className="text-white/60 text-[12px] mt-1">
                      7 subjects · Access anytime · 100% free
                    </p>
                  </div>
                </div>
                <div className="absolute -top-5 -right-5 bg-white rounded-2xl shadow-xl px-4 py-3 border border-slate-100">
                  <p className="text-[10px] text-slate-500 mb-1 font-medium">
                    Programme
                  </p>
                  <p className="text-[22px] text-[#0A2218] leading-none font-black">
                    100%
                  </p>
                  <p className="text-[10px] text-emerald-600 mt-1 font-bold">
                    FREE
                  </p>
                </div>
                <div className="absolute top-14 -left-5 bg-white rounded-2xl shadow-xl px-4 py-3 border border-slate-100">
                  <p className="text-[10px] text-slate-500 mb-1 font-medium">
                    Subjects
                  </p>
                  <p className="text-[22px] text-[#0A2218] leading-none font-black">
                    7 Core
                  </p>
                  <p className="text-[10px] text-emerald-600 mt-1 font-bold">
                    ✓ SPM 2026
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── 2. BENEFITS ───────────────────────────────────────────────────────── */}
        <section className="py-24 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Left — portal image */}
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative hidden lg:block"
              >
                <div className="rounded-3xl overflow-hidden shadow-2xl shadow-slate-200">
                  <img
                    src={IMG_PORTAL}
                    alt="Online learning portal"
                    className="w-full h-[560px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0A2218]/30 to-transparent rounded-3xl" />
                </div>
                <div className="absolute -bottom-5 -right-5 bg-emerald-600 text-white rounded-2xl p-4 shadow-xl shadow-emerald-500/30">
                  <p className="text-[10px] uppercase tracking-widest mb-1 font-bold">
                    Completely Free
                  </p>
                  <p className="text-[22px] leading-none font-black">
                    100% FREE
                  </p>
                </div>
                <div className="absolute -top-4 -left-4 bg-white rounded-2xl shadow-[0_8px_24px_rgba(16,185,129,0.15)] p-3.5 border-2 border-emerald-300">
                  <p className="text-[11px] text-emerald-500 mb-0.5 font-bold uppercase tracking-wide">
                    Includes
                  </p>
                  <p className="text-[14px] text-slate-900 font-extrabold">
                    Free Revision Notes
                  </p>
                </div>
              </motion.div>

              {/* Right — benefits list */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-10"
                >
                  <p className="text-[13px] text-emerald-700 uppercase tracking-widest mb-3 font-bold">
                    What You Will Get
                  </p>
                  <h2 className="text-[36px] lg:text-[44px] text-slate-900 tracking-tight leading-tight font-black">
                    Everything You Need to Prepare Smarter for SPM
                  </h2>
                </motion.div>
                <div className="flex flex-col gap-3">
                  {BENEFITS.map((b, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.07 }}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-sm hover:-translate-y-0.5 transition-all"
                    >
                      <div className="w-10 h-10 rounded-xl bg-white border border-slate-100 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <b.icon className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <h3 className="text-[15px] text-slate-900 font-extrabold">
                          {b.title}
                        </h3>
                        <p className="text-[13px] text-slate-500 leading-snug mt-0.5">
                          {b.desc}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. PAIN POINTS ────────────────────────────────────────────────────── */}
        <section className="py-20" style={{ backgroundColor: DARK }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-[13px] text-emerald-300 uppercase tracking-widest mb-3 font-bold">
                Are You Facing These Problems?
              </p>
              <h2 className="text-[32px] lg:text-[42px] text-white tracking-tight font-black">
                SPM Revision Doesn't Have to Feel Overwhelming
              </h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {PAIN_POINTS.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="rounded-2xl overflow-hidden"
                >
                  <div className="bg-red-500/20 border border-red-500/30 p-5">
                    <p className="text-[17px] text-white font-extrabold">
                      ❌ {p.pain}
                    </p>
                    <p className="text-[13px] text-white/60 mt-1 font-normal">
                      {p.desc}
                    </p>
                  </div>
                  <div className="bg-white/10 border border-white/15 p-5 rounded-b-2xl">
                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                      <p className="text-[14px] text-white/85 leading-relaxed font-medium">
                        {p.solution}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center mt-10"
            >
              <button
                onClick={scrollToForm}
                className="px-8 py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-[15px] font-extrabold hover:shadow-emerald-500/40 hover:-translate-y-0.5 transition-all shadow-xl shadow-emerald-500/20 cursor-pointer"
              >
                Register Now — It's Free!
              </button>
            </motion.div>
          </div>
        </section>

        {/* ── 4. PROGRAM DETAILS ────────────────────────────────────────────────── */}
        {/* <section className="py-16 bg-emerald-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <p className="text-[13px] text-emerald-700 uppercase tracking-widest mb-3 font-bold">
              Programme Details
            </p>
            <h2 className="text-[30px] lg:text-[38px] text-slate-900 tracking-tight font-black">
              2026 SPM Revision Notes & Online Revision Class
            </h2>
            <p className="text-[14px] text-slate-500 mt-2">
              Students can access the learning materials online and revise at
              their own pace.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: <Calendar className="w-6 h-6 text-emerald-500" />,
                label: "Available From",
                value: "October 2026",
              },
              {
                icon: <Monitor className="w-6 h-6 text-emerald-500" />,
                label: "Platform",
                value: "SCS Online Study Portal",
              },
              {
                icon: <PlayCircle className="w-6 h-6 text-emerald-500" />,
                label: "Format",
                value: "Recorded Lessons + Notes + Tips",
              },
              {
                icon: <Award className="w-6 h-6 text-emerald-500" />,
                label: "Programme Fee",
                value: "100% Free",
              },
            ].map((d, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col items-center text-center gap-3"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                  {d.icon}
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mb-1">
                    {d.label}
                  </p>
                  <p className="text-[14px] text-slate-900 font-extrabold leading-snug">
                    {d.value}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

        {/* ── 5. SUBJECTS ───────────────────────────────────────────────────────── */}
        {/* <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[13px] text-emerald-700 uppercase tracking-widest mb-3 font-bold">
              7 SPM Core Subjects
            </p>
            <h2 className="text-[32px] lg:text-[40px] text-slate-900 tracking-tight font-black">
              Choose the Subjects You Want to Revise
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SUBJECTS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all"
              >
                <div className="h-1.5" style={{ backgroundColor: s.color }} />
                <div className="p-5">
                  <div
                    className="w-12 h-12 rounded-xl mb-3 p-2.5 flex-shrink-0"
                    style={{ backgroundColor: s.color }}
                  >
                    {SUBJECT_ICONS[s.name]}
                  </div>
                  <h3 className="text-[15px] text-slate-900 font-extrabold mb-1">
                    {s.name}
                  </h3>
                  <p className="text-[12px] text-slate-500 font-medium">
                    {s.teacher}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

        {/* ── 6. TEACHERS ───────────────────────────────────────────────────────── */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-[13px] text-emerald-700 uppercase tracking-widest mb-3 font-bold">
                Meet the Teachers
              </p>
              <h2 className="text-[32px] lg:text-[40px] text-slate-900 tracking-tight font-black">
                Learn from Experienced SPM Educators
              </h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {TEACHERS.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
                >
                  <div className="h-1.5" style={{ backgroundColor: t.color }} />
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-[18px] font-black flex-shrink-0 overflow-hidden">
                        <img src={t.avatar} alt={t.name} />
                      </div>
                      <div>
                        <h3 className="text-[15px] text-slate-900 font-extrabold leading-tight">
                          {t.name}
                        </h3>
                        <span
                          className="inline-block mt-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold text-white"
                          style={{ backgroundColor: t.color }}
                        >
                          {t.subject}
                        </span>
                      </div>
                    </div>
                    <ul className="flex flex-col gap-1.5">
                      {t.credentials.map((c, j) => (
                        <li
                          key={j}
                          className="flex items-start gap-2 text-[12px] text-slate-600"
                        >
                          <span
                            className="mt-[3px] w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: t.color }}
                          />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 7. TESTIMONIALS ───────────────────────────────────────────────────── */}
        <section
          className="py-20 relative overflow-hidden"
          style={{ backgroundColor: DARK }}
        >
          <div className="absolute inset-0">
            <img
              src={IMG_TESTIMONIAL_BG}
              alt=""
              className="w-full h-full object-cover opacity-[0.06]"
            />
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to bottom, ${DARK}, ${DARK}cc)`,
              }}
            />
          </div>
          <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-[13px] text-emerald-300 uppercase tracking-widest mb-3 font-bold">
                Student Testimonials
              </p>
              <h2 className="text-[32px] lg:text-[40px] text-white tracking-tight font-black">
                What Students Say About Their Learning Experience
              </h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {TESTIMONIALS.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className={`bg-white/8 backdrop-blur-sm border border-white/10 rounded-2xl p-5 ${i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}`}
                >
                  <StarRating count={t.stars} />
                  <p className="text-[14px] text-white/80 leading-relaxed mt-3 mb-4">
                    {t.text}
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-white/10">
                    <div className="w-9 h-9 rounded-full bg-emerald-500/30 flex items-center justify-center text-emerald-200 text-[13px] font-bold flex-shrink-0">
                      {t.name[0]}
                    </div>
                    <div>
                      <p className="text-[14px] text-white font-bold">
                        {t.name}
                      </p>
                      <p className="text-[12px] text-white/50">{t.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 8. HOW TO REGISTER ────────────────────────────────────────────────── */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-14"
            >
              <p className="text-[13px] text-emerald-700 uppercase tracking-widest mb-3 font-bold">
                How to Register
              </p>
              <h2 className="text-[32px] lg:text-[40px] text-slate-900 tracking-tight font-black">
                Join in 4 Simple Steps
              </h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  step: "01",
                  title: "Fill in the Registration Form",
                  desc: "Complete your details and select the subject or subjects you would like to join.",
                },
                {
                  step: "02",
                  title: "Join Our WhatsApp Community",
                  desc: "After submitting, you will be redirected to WhatsApp to join the SCS community for updates.",
                },
                {
                  step: "03",
                  title: "Get Access to the Learning Portal",
                  desc: "Our SCS Education Counsellor will contact you and guide you through the portal access process.",
                },
                {
                  step: "04",
                  title: "Start Your Revision",
                  desc: "Access revision notes, recorded lessons and exam tips through the SCS Online Study Portal anytime.",
                },
              ].map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex flex-col items-center text-center"
                >
                  <div className="w-20 h-20 mb-4">{STEP_ILLUSTRATIONS[i]}</div>
                  <div className="w-8 h-8 rounded-full bg-emerald-500 text-white text-[13px] font-black flex items-center justify-center mb-3">
                    {s.step}
                  </div>
                  <h3 className="text-[15px] text-slate-900 font-extrabold mb-2 leading-snug">
                    {s.title}
                  </h3>
                  <p className="text-[13px] text-slate-500 leading-relaxed">
                    {s.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 9. FAQ ────────────────────────────────────────────────────────────── */}
        <section className="py-20" style={{ backgroundColor: DARK }}>
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-[13px] text-emerald-300 uppercase tracking-widest mb-3 font-bold">
                FAQ
              </p>
              <h2 className="text-[32px] lg:text-[40px] text-white tracking-tight font-black">
                Frequently Asked Questions
              </h2>
            </motion.div>
            <div className="bg-white/5 border border-white/10 rounded-2xl px-6 divide-y divide-white/10">
              {FAQS.map((f, i) => (
                <FAQItem key={i} q={f.q} a={f.a} />
              ))}
            </div>
          </div>
        </section>

        {/* ── 10. REGISTRATION FORM ─────────────────────────────────────────────── */}
        <section className="py-20 bg-emerald-50" ref={formRef}>
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <p className="text-[13px] text-emerald-700 uppercase tracking-widest mb-3 font-bold">
                Register Now
              </p>
              <h2 className="text-[32px] lg:text-[40px] text-slate-900 tracking-tight font-black">
                Register for FREE Today
              </h2>
              <p className="text-[15px] text-slate-500 mt-2">
                Study Smart. Prepare Early. Score Better.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden"
            >
              <RegistrationForm id="spm-registration-form" />
            </motion.div>
          </div>
        </section>

        {/* ── 11. FINAL CTA ─────────────────────────────────────────────────────── */}
        <section
          className="py-20 relative overflow-hidden"
          style={{ backgroundColor: DARK }}
        >
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-blue-400/10 blur-3xl" />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-[13px] text-emerald-300 uppercase tracking-widest mb-4 font-bold">
                Start Preparing for SPM Early
              </p>
              <h2 className="text-[36px] lg:text-[52px] text-white tracking-tight font-black mb-4 leading-tight">
                Don't Wait Until the Last Minute.
              </h2>
              <p className="text-[17px] text-white/65 mb-10 max-w-2xl mx-auto leading-relaxed">
                Build a stronger foundation, revise more effectively and prepare
                yourself with the right learning materials before SPM.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10 max-w-3xl mx-auto"
            >
              {[
                "7 SPM Core Subjects",
                "Experienced Teachers",
                "Revision Notes & Exam Tips",
                "Recorded Lessons · Online Portal",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2.5 bg-white/8 border border-white/10 rounded-xl px-4 py-3"
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-[13px] text-white/90 font-medium text-left leading-snug">
                    {item}
                  </span>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="inline-block bg-white/10 border border-white/20 rounded-2xl px-8 py-4 mb-8">
                <p className="text-[42px] text-white font-black leading-none">
                  100% FREE
                </p>
                <p className="text-[14px] text-emerald-300 font-bold mt-1">
                  2026 SPM Revision Class
                </p>
              </div>
              <br />
              <button
                onClick={scrollToForm}
                className="px-10 py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-[16px] font-extrabold shadow-2xl shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-1 transition-all inline-flex items-center gap-2 cursor-pointer"
              >
                🎓 Register for FREE <ArrowRight className="w-5 h-5" />
              </button>
              <p className="text-[13px] text-white/40 mt-4">
                Limited availability may apply.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 py-8 px-6 lg:px-12 text-center">
          <p className="text-[13px] text-slate-500">
            © 2026 SCS Education. All rights reserved.
          </p>
        </footer>
      </div>
    </>
  );
}
